import React from 'react';
import {
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import styles from '../MessagesTemplates.module.css';
import { useWhatsAppSender } from '../../../contexts/WhatsAppSenderContext';
import fillTemplateExample from './fillTemplateExample';

const TemplateCard = ({
    template,
    category,
    onEdit,
    onPreview,
    onCopy
}) => {
    const { capabilities } = useWhatsAppSender();

    const getCategoryClass = (category) => {
        switch(category) {
            case 'agendamento': return styles.agendamento;
            case 'confirmacoes': return styles.confirmacoes;
            case 'lembretes': return styles.lembretes;
            case 'especiais': return styles.especiais;
            case 'feedback': return styles.feedback;
            default: return '';
        }
    };

    const formatPreview = (content) => {
        if (!content) return '';
        
        // Remove HTML tags for preview e mostra um exemplo preenchido, sem as variáveis
        const cleanContent = fillTemplateExample(content).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        
        // Limit to approximately 120 characters for better preview
        if (cleanContent.length > 120) {
            return cleanContent.substring(0, 120) + '...';
        }
        
        return cleanContent;
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(template.TemplateContent);
            if (onCopy) {
                onCopy(template);
            }
        } catch {}
    };

    return (
        <div className={`${styles.templateCard} ${getCategoryClass(category)}`}>
            <div className={styles.cardHeader}>
                <h3 className={styles.templateName}>
                    {getTemplateDisplayName(template.TemplateKey)}
                </h3>
                
                <div className={styles.templateTags}>
                    {template.isFallback ? (
                        <Chip
                            label="Padrão"
                            size="small"
                            className={`${styles.templateTag} ${styles.default}`}
                            color="primary"
                            variant="filled"
                        />
                    ) : (
                        <Chip
                            label="Customizada"
                            size="small"
                            className={`${styles.templateTag} ${styles.custom}`}
                            color="secondary"
                            variant="filled"
                        />
                    )}
                </div>
            </div>

            <div className={styles.templatePreview}>
                <div className={styles.previewContent}>
                    {formatPreview(template.TemplateContent)}
                </div>
            </div>

            <div className={styles.cardFooter}>
                <div className={styles.cardActions}>
                    <Tooltip title="Ver mensagem completa">
                        <IconButton
                            size="small"
                            onClick={() => onPreview && onPreview(template)}
                            color="primary"
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Copiar conteúdo">
                        <IconButton
                            size="small"
                            onClick={handleCopy}
                            color="secondary"
                        >
                            <CopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {capabilities.template_edit && (
                        <Tooltip title="Editar modelo">
                            <IconButton
                                size="small"
                                onClick={() => onEdit && onEdit(template)}
                                color="primary"
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

// Helper function to get user-friendly template names
const getTemplateDisplayName = (key) => {
    const displayNames = {
        'AppointmentCreatedFullMessage': 'Confirmação de Agendamento (Completa)',
        'AppointmentCreatedShortMessage': 'Confirmação de Agendamento (Resumida)',
        'AppointmentAskForConfirmation1': 'Primeira Confirmação',
        'AppointmentAskForConfirmation2': 'Segunda Confirmação',
        'AppointmentAskForConfirmation3': 'Terceira Confirmação',
        'Appointment7DaysReminder': 'Lembrete 7 Dias',
        'AppointmentReservationMessage': 'Reserva de Horário',
        'AppointmentReservationReminder': 'Lembrete de Cadastro da Reserva',
        'AppointmentReservationReleased': 'Reserva Liberada',
        'AppointmentCanceledMessage': 'Cancelamento de Agendamento',
        'AppointmentCancellationDueLackOfReturn': 'Cancelamento por Falta de Retorno',
        'SuggestDate': 'Sugestão de Data',
        'HappyBirthday': 'Feliz Aniversário',
        'SuggestDateFromWaitingList': 'Sugestão da Lista de Espera',
        'AppointmentRescheduledConfirmedMessage': 'Reagendamento Confirmado',
        'CanceledAppointmentFollowUp': 'Follow-up Agendamento Cancelado',
        'FirstAppointmentFollowup': 'Follow-up Primeiro Agendamento',
        'NPSFeedbackRequest': 'Solicitação de Feedback NPS',
        'GoogleReviewRequest': 'Solicitação de Avaliação Google',
        'AppointmentAssessmentTemplate': 'Avaliação do Agendamento',
        'AppointmentEvolutionTemplate': 'Evolução do Tratamento'
    };
    
    return displayNames[key] || key;
};

export default TemplateCard;